import type { ColDef, ColGroupDef } from 'ag-grid-community';

import type { GatedBlurResolveParams, GatedBlurResolved } from '../lib/interfaces';

const EARNINGS_FIELDS_SHARP_IF_FREE = new Set(['ticker', 'name', 'period', 'eps_prior', 'revenue_prior', 'revenue']);

const EARNINGS_FIELDS_ALL = [
  'ticker',
  'name',
  'period',
  'eps_prior',
  'eps_est',
  'eps',
  'eps_surprise_percent',
  'revenue_prior',
  'revenue_est',
  'revenue',
  'revenue_surprise_percent',
  'date',
  'get_alert',
] as const;

const EARNINGS_PARTIAL_BLUR_FIELDS = new Set<string>(
  EARNINGS_FIELDS_ALL.filter(f => !EARNINGS_FIELDS_SHARP_IF_FREE.has(f)),
);

function appendGatedCellClass(existing: ColDef['cellClass']): ColDef['cellClass'] {
  const token = 'gated-cell';
  const withString = (s: string) => (s.includes(token) ? s : `${s} ${token}`.trim());
  const withArray = (classes: string[]) => (classes.includes(token) ? classes : [...classes, token]);

  if (!existing) return token;
  if (typeof existing === 'string') return withString(existing);
  if (Array.isArray(existing)) return withArray(existing);

  const fn = existing as (p: unknown) => string | string[] | undefined;
  return ((params: unknown) => {
    const base = fn(params);
    if (Array.isArray(base)) return withArray(base);
    return withString(String(base ?? ''));
  }) as ColDef['cellClass'];
}

function earningsColumnDefsWithPartialBlur(columnDefs: (ColDef | ColGroupDef)[] | undefined) {
  if (!columnDefs?.length) return [];
  return columnDefs.map(col => {
    const c = col as ColDef;
    const field = c.field as string | undefined;
    if (!field || !EARNINGS_PARTIAL_BLUR_FIELDS.has(field)) return col;
    return { ...c, cellClass: appendGatedCellClass(c.cellClass) };
  });
}

function earningsTableHeaderConfig(
  calendar: string | undefined,
  hasUnlimitedCalendars: boolean | undefined,
  isPaywallActive: boolean | undefined,
) {
  if (calendar !== 'earnings' || !isPaywallActive) return {};
  return { restrictTableHeader: !hasUnlimitedCalendars };
}

export function resolveEarningsGatedBlur(params: GatedBlurResolveParams): GatedBlurResolved | null {
  const { blurColumnsWhenGated, calendar, columnDefs, hasUnlimitedCalendars, isGated, isLoggedIn, isPaywallActive } =
    params;

  if (calendar !== 'earnings') return null;

  const tableHeaderConfig = earningsTableHeaderConfig(calendar, hasUnlimitedCalendars, isPaywallActive);

  if (!isGated || !isPaywallActive) {
    return {
      blurColumnsWhenGatedResolved: blurColumnsWhenGated,
      columnDefsResolved: columnDefs,
      fullTableBlur: false,
      ...tableHeaderConfig,
    };
  }

  if (!isLoggedIn) {
    return {
      blurColumnsWhenGatedResolved: blurColumnsWhenGated,
      columnDefsResolved: columnDefs,
      fullTableBlur: true,
      ...tableHeaderConfig,
    };
  }

  return {
    blurColumnsWhenGatedResolved: EARNINGS_PARTIAL_BLUR_FIELDS,
    columnDefsResolved: earningsColumnDefsWithPartialBlur(columnDefs),
    fullTableBlur: false,
    useColumnDefsForServerSide: true,
    ...tableHeaderConfig,
  };
}
